import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const stockAPI = {
  catalog: () => api.get('/stock/catalog'),
  createProduct: (d) => api.post('/stock/catalog', d),
  updateProduct: (id, d) => api.put(`/stock/catalog/${id}`, d),
  archiveProduct: (id) => api.delete(`/stock/catalog/${id}`),
  balances: (params) => api.get('/stock/balances', { params }),
  balance: (id) => api.get(`/stock/balances/${id}`),
  settings: (id, d) => api.put(`/stock/balances/${id}/settings`, d),
  count: (id, d) => api.post(`/stock/balances/${id}/count`, d),
  validateObservation: (id, quantity) => api.post(`/stock/observations/${id}/validate`, { quantity }),
  transferPreview: (d) => api.post('/stock/transfers/preview', d),
  transfer: (d) => api.post('/stock/transfers', d),
  movements: (params) => api.get('/stock/movements', { params }),
  rules: () => api.get('/stock/rules'),
  createRule: (d) => api.post('/stock/rules', d),
  deleteRule: (id) => api.delete(`/stock/rules/${id}`),
  setHorizon: (horizon_days) => api.put('/stock/settings', { horizon_days }),
  forecast: (params) => api.get('/stock/forecast', { params }),
  alerts: () => api.get('/stock/alerts'),
  watchlist: () => api.get('/stock/watchlist'),
  generateOrder: (d) => api.post('/stock/orders/generate', d),
  orders: () => api.get('/stock/orders'),
  order: (id) => api.get(`/stock/orders/${id}`),
  editOrder: (id, d) => api.put(`/stock/orders/${id}`, d),
  setOrderStatus: (id, status) => api.post(`/stock/orders/${id}/status`, { status }),
  receive: (id, d) => api.post(`/stock/orders/${id}/receptions`, d),
  orderPdf: (id) => api.get(`/stock/orders/${id}/pdf`, { responseType: 'blob' }),
  shareOrder: (id, d) => api.post(`/stock/orders/${id}/share`, d),
  missionStock: (mid) => api.get(`/stock/mission/${mid}`),
  observe: (mid, d) => api.post(`/stock/mission/${mid}/observations`, d),
  observationPhoto: (mid, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/stock/mission/${mid}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const ownerPulseAPI = {
  landlords: () => api.get('/owner-pulse/landlords'),
  createLandlord: (d) => api.post('/owner-pulse/landlords', d),
  updateLandlord: (id, d) => api.put(`/owner-pulse/landlords/${id}`, d),
  deleteLandlord: (id) => api.delete(`/owner-pulse/landlords/${id}`),
  reports: () => api.get('/owner-pulse/reports'),
  report: (id) => api.get(`/owner-pulse/reports/${id}`),
  createReport: (d) => api.post('/owner-pulse/reports', d),
  updateReport: (id, d) => api.put(`/owner-pulse/reports/${id}`, d),
  recompute: (id) => api.post(`/owner-pulse/reports/${id}/recompute`),
  schedule: (id, d) => api.post(`/owner-pulse/reports/${id}/schedule`, d),
  unschedule: (id) => api.post(`/owner-pulse/reports/${id}/unschedule`),
  send: (id, d) => api.post(`/owner-pulse/reports/${id}/send`, d),
  pdf: (id) => api.get(`/owner-pulse/reports/${id}/pdf`, { responseType: 'blob' }),
  deleteReport: (id) => api.delete(`/owner-pulse/reports/${id}`),
};

export const newOpId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Payments
export const paymentsAPI = {
  createCheckout: (originUrl) => api.post('/payments/create-checkout', { origin_url: originUrl }),
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  syncSubscription: () => api.post('/payments/sync-subscription'),
  cancelSubscription: (immediate = false) => api.post('/payments/cancel-subscription', { immediate }),
};

// Properties
export const propertiesAPI = {
  getAll: () => api.get('/properties'),
  getOne: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  patch: (id, data) => api.patch(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  assignProvider: (propertyId, providerId) => api.post(`/properties/${propertyId}/assign-provider/${providerId}`),
  syncIcal: (propertyId, icalUrls = {}) => api.post(`/properties/${propertyId}/sync-ical`, icalUrls),
  getReservations: (propertyId) => api.get(`/properties/${propertyId}/reservations`),
  uploadPhoto: (propertyId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/upload/property-photo/${propertyId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};

// Cleaning Photos
export const cleaningPhotosAPI = {
  getAll: (propertyId) => api.get(`/properties/${propertyId}/cleaning-photos`),
  upload: (propertyId, file, comment) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('comment', comment || '');
    return api.post(`/properties/${propertyId}/cleaning-photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (propertyId, photoId) => api.delete(`/properties/${propertyId}/cleaning-photos/${photoId}`),
};

// Providers
export const providersAPI = {
  getAll: () => api.get('/providers'),
  create: (data) => api.post('/providers', data),
  delete: (id) => api.delete(`/providers/${id}`),
};

// Missions
export const missionsAPI = {
  getAll: (params) => api.get('/missions', { params }),
  getOne: (id) => api.get(`/missions/${id}`),
  create: (data) => api.post('/missions', data),
  update: (id, data) => api.put(`/missions/${id}`, data),
  validate: (id, data) => api.post(`/missions/${id}/validate`, data),
  getProviderMissions: () => api.get('/provider/missions'),
};

// Provider Dashboard
export const providerAPI = {
  getDashboard: () => api.get('/provider/dashboard'),
  getProperties: () => api.get('/provider/properties'),
  getReservations: () => api.get('/provider/reservations'),
  getMissions: () => api.get('/provider/missions'),
  addComment: (data) => api.post('/provider/task-comment', data),
  getComments: (propertyId) => api.get(`/provider/comments/${propertyId}`),
};

// Owner Comments API
export const ownerCommentsAPI = {
  getAll: () => api.get('/owner/all-comments'),
  markRead: (commentId) => api.put(`/owner/comments/${commentId}/read`),
};

// Reservations
export const reservationsAPI = {
  getAll: (params) => api.get('/reservations', { params }),
  getUpcoming: () => api.get('/reservations/upcoming'),
  syncAll: () => api.post('/sync-all-properties'),
};

// Financials
export const financialsAPI = {
  getAll: (params) => api.get('/financials', { params }),
  create: (data) => api.post('/financials', data),
  delete: (id) => api.delete(`/financials/${id}`),
  getSummary: (params) => api.get('/financials/summary', { params }),
  getStats: () => api.get('/financials/stats'),
  importFromReservations: (propertyId) => api.post('/financials/import-from-reservations', { property_id: propertyId }),
};

// Dashboard
export const dashboardAPI = {
  get: () => api.get('/dashboard'),
};

// Guide (public)
export const guideAPI = {
  get: (propertyId) => axios.get(`${API_URL}/api/guide/${propertyId}`),
};

// AI Assistant
export const aiAPI = {
  chat: (message, context) => api.post('/ai/chat', { message, context }),
};

// Receipts (Quittances de loyer)
export const receiptsAPI = {
  getAll: () => api.get('/receipts'),
  getOne: (id) => api.get(`/receipts/${id}`),
  create: (data) => api.post('/receipts', data),
  delete: (id) => api.delete(`/receipts/${id}`),
  send: (id, email, customMessage) => api.post(`/receipts/${id}/send`, { email, custom_message: customMessage }),
  pdfUrl: (id) => `${API_URL}/api/receipts/${id}/pdf`,
};

// Financial Documents (5 types unifiés)
export const documentsAPI = {
  getAll: (params = {}) => api.get('/financial-documents', { params }),
  getOne: (id) => api.get(`/financial-documents/${id}`),
  create: (data) => api.post('/financial-documents', data),
  update: (id, data) => api.put(`/financial-documents/${id}`, data),
  delete: (id) => api.delete(`/financial-documents/${id}`),
  send: (id, email, customMessage) => api.post(`/financial-documents/${id}/send`, { email, custom_message: customMessage }),
  duplicate: (id) => api.post(`/financial-documents/${id}/duplicate`),
  cancel: (id, reason) => api.post(`/financial-documents/${id}/cancel`, { reason }),
  markPaid: (id) => api.post(`/financial-documents/${id}/mark-paid`),
  pdfUrl: (id) => `${API_URL}/api/financial-documents/${id}/pdf`,
};

// État des Lieux
export const edlAPI = {
  getAll: () => api.get('/etat-des-lieux'),
  getOne: (id) => api.get(`/etat-des-lieux/${id}`),
  create: (data) => api.post('/etat-des-lieux', data),
  update: (id, data) => api.put(`/etat-des-lieux/${id}`, data),
  delete: (id) => api.delete(`/etat-des-lieux/${id}`),
  send: (id, email, customMessage) => api.post(`/etat-des-lieux/${id}/send`, { email, custom_message: customMessage }),
  pdfUrl: (id) => `${API_URL}/api/etat-des-lieux/${id}/pdf`,
  uploadPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/etat-des-lieux/upload-photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export default api;
